import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell
} from 'recharts';
import './StatisticsPage.css';
import { ReactComponent as CountIcon } from '../icon/次数.svg';
import { ReactComponent as MemberIcon } from '../icon/人数.svg';
import { ReactComponent as RevenueIcon } from '../icon/总收入.svg';

const COLORS = [
  '#FF6384', // Red
  '#36A2EB', // Blue
  '#FFCE56', // Yellow
  '#4BC0C0', // Green
  '#9966FF', // Purple
  '#FF9F40', // Orange
  '#8EFF6C', // Light Green
  '#008000', // Dark Green
  '#000080', // Navy
  '#808080', // Gray
];

function StatisticsPage({ records }) {
  const [monthChartType, setMonthChartType] = useState('line');
  const [projectChartType, setProjectChartType] = useState('bar');
  const [methodChartType, setMethodChartType] = useState('pie');
  const [selectedYear, setSelectedYear] = useState(null);

  const [lessonCount, setLessonCount] = useState(0);
  const [uniqueStudentsCount, setUniqueStudentsCount] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);

  useEffect(() => {
    const lessonCount = records.length;
    const uniqueStudents = new Set(records.map(record => record.person));
    const uniqueStudentsCount = uniqueStudents.size;
    const totalRevenue = records.reduce((sum, record) => {
      const fee = parseFloat(record.fee);
      return sum + (isNaN(fee) ? 0 : fee);
    }, 0);

    setLessonCount(lessonCount);
    setUniqueStudentsCount(uniqueStudentsCount);
    setTotalRevenue(totalRevenue);

    // Extract available years from records and set default to the latest year
    const years = new Set();
    records.forEach(record => {
      const year = new Date(record.record_time).getFullYear();
      years.add(year);
    });
    if (years.size > 0) {
      const sortedYears = Array.from(years).sort((a, b) => b - a);
      setSelectedYear(sortedYears[0]); // Set to the latest year
    }
  }, [records]);

  // Function to get available years from records
  const getAvailableYears = () => {
    const years = new Set();
    records.forEach(record => {
      const year = new Date(record.record_time).getFullYear();
      years.add(year);
    });
    return Array.from(years).sort((a, b) => b - a);
  };

  // Function to get statistics for selected year
  const getYearStatistics = (records, year) => {
    const filteredRecords = year 
      ? records.filter(record => new Date(record.record_time).getFullYear() === year)
      : records;

    const lessonCount = filteredRecords.length;
    const totalFee = filteredRecords.reduce((sum, record) => {
      const fee = parseFloat(record.fee);
      return sum + (isNaN(fee) ? 0 : fee);
    }, 0);

    return {
      lessonCount,
      totalFee
    };
  };

  // Function to aggregate data by month
  const aggregateDataByMonth = (records, year) => {
    // Filter records by the selected year if provided
    const filteredRecords = year 
      ? records.filter(record => new Date(record.record_time).getFullYear() === year)
      : records;

    const monthlyDataMap = new Map();

    filteredRecords.forEach(record => {
      const recordTime = new Date(record.record_time);
      const month = recordTime.toLocaleString('default', { month: 'long' });
      const monthNumber = recordTime.getMonth(); // Get month number for sorting

      if (!monthlyDataMap.has(month)) {
        monthlyDataMap.set(month, {
          month: month,
          monthNumber: monthNumber,
          lessonCount: 0,
          totalFee: 0,
        });
      }

      const data = monthlyDataMap.get(month);
      data.lessonCount++;
      data.totalFee += parseFloat(record.fee) || 0;
    });

    const monthlyDataArray = Array.from(monthlyDataMap.values());
    // Sort the monthlyData array by month number
    monthlyDataArray.sort((a, b) => a.monthNumber - b.monthNumber);

    return monthlyDataArray;
  };

  // Function to aggregate data by project
  const aggregateDataByProject = (records, year) => {
    // Filter records by the selected year if provided
    const filteredRecords = year 
      ? records.filter(record => new Date(record.record_time).getFullYear() === year)
      : records;

    const projectDataMap = new Map();

    filteredRecords.forEach(record => {
      const projects = record.content || [];
      projects.forEach(project => {
        if (!projectDataMap.has(project)) {
          projectDataMap.set(project, {
            project: project,
            lessonCount: 0,
            totalFee: 0,
          });
        }

        const data = projectDataMap.get(project);
        data.lessonCount++;
        data.totalFee += parseFloat(record.fee) || 0;
      });
    });

    return Array.from(projectDataMap.values());
  };

  // Function to aggregate data by method
  const aggregateDataByMethod = (records, year) => {
    // Filter records by the selected year if provided
    const filteredRecords = year 
      ? records.filter(record => new Date(record.record_time).getFullYear() === year)
      : records;

    const methodDataMap = new Map();

    filteredRecords.forEach(record => {
      const method = record.method || '未知';

      if (!methodDataMap.has(method)) {
        methodDataMap.set(method, {
          method: method,
          lessonCount: 0,
          totalFee: 0,
        });
      }

      const data = methodDataMap.get(method);
      data.lessonCount++;
      data.totalFee += parseFloat(record.fee) || 0;
    });

    return Array.from(methodDataMap.values());
  };

  // Process records data for charts
  const monthlyData = aggregateDataByMonth(records, selectedYear);
  const projectData = aggregateDataByProject(records, selectedYear);
  const methodData = aggregateDataByMethod(records, selectedYear);

  const handleChartTypeChange = (event, chartTypeSetter) => {
    chartTypeSetter(event.target.value);
  };

  const renderChart = (data, dataKey, nameKey, chartType) => {
    const commonProps = {
      data: data,
      margin: { top: 10, right: 30, left: -10, bottom: 5 },
    };

    const tooltipFormatter = (value, name, props) => {
      if (name === '费用') return `¥${value.toFixed(2)}`;
      return value;
    };

    switch (chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={nameKey} tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={tooltipFormatter} />
              <Legend />
              <Line
                type="monotone"
                dataKey={dataKey}
                name={dataKey === 'totalFee' ? '费用' : '课程数量'}
                stroke="#8884d8"
                activeDot={{ r: 8 }}
                label
              />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={nameKey} tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={tooltipFormatter} />
              <Legend />
              <Bar dataKey={dataKey} name={dataKey === 'totalFee' ? '费用' : '课程数量'}>
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                dataKey={dataKey}
                nameKey={nameKey}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={tooltipFormatter} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <div className="headerContainer">
        <h2>统计图表</h2>
        <Link to="/">返回列表</Link>
        <div className="statistics">
          <span title="上课人数"><MemberIcon width="20" height="20" />{uniqueStudentsCount}</span>
          <span title="上课课时"><CountIcon width="20" height="20" /> {lessonCount}</span>
          <span title="费用统计"><RevenueIcon width="20" height="20" /> ¥{totalRevenue.toFixed(2)}</span>
        </div>
      </div>

      {/* Chart components */}
      <div className="chart-group">
        <h3>月份</h3>
        <div className="controls-container">
          <select
            value={monthChartType}
            onChange={(e) => handleChartTypeChange(e, setMonthChartType)}
          >
            <option value="bar">柱状图</option>
            <option value="pie">饼图</option>
            <option value="line">折线图</option>
          </select>
          <select
            value={selectedYear || ''}
            onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">所有年份</option>
            {getAvailableYears().map(year => (
              <option key={year} value={year}>{year}年</option>
            ))}
          </select>
          <div className="year-statistics">
            {(() => {
              const stats = getYearStatistics(records, selectedYear);
              return (
                <>
                  <span>课程数: {stats.lessonCount}</span>
                  <span>总费用: ¥{stats.totalFee.toFixed(2)}</span>
                </>
              );
            })()}
          </div>
        </div>
        <div className="chart-pair-container">
          <div className="chart-item">
            <h4>费用</h4>
            {renderChart(monthlyData, 'totalFee', 'month', monthChartType)}
          </div>
          <div className="chart-item">
            <h4>课程数量</h4>
            {renderChart(monthlyData, 'lessonCount', 'month', monthChartType)}
          </div>
        </div>
      </div>

      <div className="chart-group">
        <h3>项目</h3>
        <div className="controls-container">
          <select
            value={projectChartType}
            onChange={(e) => handleChartTypeChange(e, setProjectChartType)}
          >
            <option value="bar">柱状图</option>
            <option value="pie">饼图</option>
            <option value="line">折线图</option>
          </select>
          <select
            value={selectedYear || ''}
            onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">所有年份</option>
            {getAvailableYears().map(year => (
              <option key={year} value={year}>{year}年</option>
            ))}
          </select>
          <div className="year-statistics">
            {(() => {
              const stats = getYearStatistics(records, selectedYear);
              return (
                <>
                  <span>课程数: {stats.lessonCount}</span>
                  <span>总费用: ¥{stats.totalFee.toFixed(2)}</span>
                </>
              );
            })()}
          </div>
        </div>
        <div className="chart-pair-container">
          <div className="chart-item">
            <h4>费用</h4>
            {renderChart(projectData, 'totalFee', 'project', projectChartType)}
          </div>
          <div className="chart-item">
            <h4>课程数量</h4>
            {renderChart(projectData, 'lessonCount', 'project', projectChartType)}
          </div>
        </div>
      </div>

      <div className="chart-group">
        <h3>上课渠道</h3>
        <div className="controls-container">
          <select
            value={methodChartType}
            onChange={(e) => handleChartTypeChange(e, setMethodChartType)}
          >
            <option value="bar">柱状图</option>
            <option value="pie">饼图</option>
            <option value="line">折线图</option>
          </select>
          <select
            value={selectedYear || ''}
            onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">所有年份</option>
            {getAvailableYears().map(year => (
              <option key={year} value={year}>{year}年</option>
            ))}
          </select>
          <div className="year-statistics">
            {(() => {
              const stats = getYearStatistics(records, selectedYear);
              return (
                <>
                  <span>课程数: {stats.lessonCount}</span>
                  <span>总费用: ¥{stats.totalFee.toFixed(2)}</span>
                </>
              );
            })()}
          </div>
        </div>
        <div className="chart-pair-container">
          <div className="chart-item">
            <h4>费用</h4>
            {renderChart(methodData, 'totalFee', 'method', methodChartType)}
          </div>
          <div className="chart-item">
            <h4>课程数量</h4>
            {renderChart(methodData, 'lessonCount', 'method', methodChartType)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatisticsPage;
